import React, { useState, useEffect } from 'react';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useResendVerificationMutation, useGetVerificationStatusQuery } from 'store/services/auth/authApi';
import './UnverifiedEmailBanner.css';

const UnverifiedEmailBanner: React.FC = () => {
  const { data: userData } = useGetMeQuery();
  const { data: emailStatus } = useGetVerificationStatusQuery();
  const [resendVerification, { isLoading: isResending }] = useResendVerificationMutation();
  const [isDismissed, setIsDismissed] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rateLimitSeconds, setRateLimitSeconds] = useState<number>(0);

  // Check if banner was previously dismissed (stored in localStorage)
  useEffect(() => {
    const dismissedUntil = localStorage.getItem('emailVerificationBannerDismissed');
    if (dismissedUntil) {
      const dismissedTime = parseInt(dismissedUntil, 10);
      const now = Date.now();
      // Keep banner dismissed for 24 hours
      if (now < dismissedTime) {
        setIsDismissed(true);
      } else {
        // Clear expired dismissal
        localStorage.removeItem('emailVerificationBannerDismissed');
      }
    }
  }, []);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (rateLimitSeconds > 0) {
      const timer = setTimeout(() => {
        setRateLimitSeconds(rateLimitSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rateLimitSeconds]);

  // Auto-clear success/error messages after 5 seconds
  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  // Don't show banner if:
  // - User data is not loaded yet
  // - Email is already verified
  // - Banner was dismissed
  if (!userData || userData.emailVerified || emailStatus?.emailVerified || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    // Dismiss for 24 hours
    const dismissUntil = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem('emailVerificationBannerDismissed', dismissUntil.toString());
    setIsDismissed(true);
  };

  const handleResendVerification = async () => {
    // Don't allow resend if already sending or rate limited
    if (isResending || rateLimitSeconds > 0) {
      return;
    }

    // Clear previous messages
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      await resendVerification().unwrap();
      setSuccessMessage('Verification email sent! Check your inbox.');

      // Backend enforces 3 requests per hour, so set local rate limit timer
      // Set to 20 minutes (1200 seconds) to be safe
      setRateLimitSeconds(1200);
    } catch (error: any) {
      console.error('Error resending verification:', error);

      // Parse rate limit error message
      // Backend returns: "Too many verification email requests. Please try again in X minutes."
      if (error?.status === 429 && error?.data?.message) {
        const message = error.data.message;
        const minutesMatch = message.match(/(\d+)\s+minute/i);

        if (minutesMatch) {
          const minutes = parseInt(minutesMatch[1], 10);
          setRateLimitSeconds(minutes * 60);
          setErrorMessage(`Too many requests. Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`);
        } else {
          setErrorMessage('Too many requests. Please try again later.');
          setRateLimitSeconds(1200); // Default to 20 minutes
        }
      } else if (error?.data?.message) {
        setErrorMessage(error.data.message);
      } else {
        setErrorMessage('Failed to send verification email. Please try again.');
      }
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const isButtonDisabled = isResending || rateLimitSeconds > 0;

  return (
    <div className="unverified-email-banner">
      <div className="banner-content">
        <div className="banner-icon">
          <img
            src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/warning.svg"
            alt="Warning"
            width="20"
            height="20"
            style={{ filter: 'brightness(0) saturate(100%) invert(62%) sepia(98%) saturate(1247%) hue-rotate(6deg) brightness(102%) contrast(95%)' }}
          />
        </div>
        <span className="banner-message">
          Your account has limited functionality until you verify your email address.
        </span>
        <div className="banner-actions">
          <button
            className="banner-verify-button"
            onClick={handleResendVerification}
            disabled={isButtonDisabled}
          >
            {isResending ? 'Sending...' : 'Resend Verification'}
          </button>
          {rateLimitSeconds > 0 && (
            <span className="rate-limit-timer">
              Available in {formatTime(rateLimitSeconds)}
            </span>
          )}
        </div>
      </div>
      <button
        className="banner-dismiss-button"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
        </svg>
      </button>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="banner-toast banner-toast-success">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="banner-toast banner-toast-error">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default UnverifiedEmailBanner;
